import { MDS_COUNTRY_CODE } from "constants/addressConfig";
import { INDIA_COUNTRY_ID } from "constants/config";

export const OMS_REGIONS = {
  INDIA: "india",
  US: "us",
  GCC: "gcc",
};

export const USA_COUNTRY_ID = 8;

export const GCC_COUNTRY_IDS = [2, 3, 4, 5, 6, 7];

const PACKAGE_API_PATHS = {
  LEARN_PACKAGES: "/open/features/v3/get_packages",
  SCA_PACKAGES: "/open/features/v2/get_packages",
  PACKAGES_AND_SUBSCRIPTIONS: "/one_oms/getPackagesAndSubscriptions",
};

/** Stable ISD → region map for phone-number search. */
export const OMS_REGION_BY_ISD_CODE = {
  "+91": OMS_REGIONS.INDIA,
  "+1": OMS_REGIONS.US,
  "+971": OMS_REGIONS.GCC,
  "+966": OMS_REGIONS.GCC,
  "+974": OMS_REGIONS.GCC,
  "+968": OMS_REGIONS.GCC,
  "+965": OMS_REGIONS.GCC,
  "+973": OMS_REGIONS.GCC,
};

/** Country code → region map for customer-ID country dropdown. */
export const OMS_REGION_BY_COUNTRY_CODE = {
  [MDS_COUNTRY_CODE.INDIA]: OMS_REGIONS.INDIA,
  [MDS_COUNTRY_CODE.USA]: OMS_REGIONS.US,
  [MDS_COUNTRY_CODE.BAHRAIN]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.KUWAIT]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.QATAR]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.OMAN]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.SAUDI_ARABIA]: OMS_REGIONS.GCC,
  [MDS_COUNTRY_CODE.UAE]: OMS_REGIONS.GCC,
};

const OMS_REGION_BY_COUNTRY_ID = {
  [Number(INDIA_COUNTRY_ID)]: OMS_REGIONS.INDIA,
  [USA_COUNTRY_ID]: OMS_REGIONS.US,
  ...Object.fromEntries(
    GCC_COUNTRY_IDS.map((countryId) => [countryId, OMS_REGIONS.GCC]),
  ),
};

const trimBaseUrl = (url) => {
  if (url == null) {
    return "";
  }
  const trimmed = String(url).trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.replace(/\/+$/, "");
};

const getIndiaPackageApiBaseUrl = () => {
  const configured = trimBaseUrl(process.env.REACT_APP_OMS_PACKAGE_API_BASE_URL);
  if (configured) {
    return configured;
  }

  const learnPackagesUrl = process.env.REACT_APP_OMS_GET_LEARN_PACKAGES_URL;
  if (!learnPackagesUrl) {
    return "";
  }

  try {
    return new URL(learnPackagesUrl).origin;
  } catch (error) {
    return "";
  }
};

export const OMS_BASE_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_URL),
  [OMS_REGIONS.US]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_US_URL),
  [OMS_REGIONS.GCC]: trimBaseUrl(process.env.REACT_APP_ONE_OMS_GCC_URL),
};

/** Full unified search-user URLs per region (includes path). */
export const SEARCH_USER_URL_BY_REGION = {
  [OMS_REGIONS.INDIA]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_URL,
  ),
  [OMS_REGIONS.US]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_US_URL,
  ),
  [OMS_REGIONS.GCC]: trimBaseUrl(
    process.env.REACT_APP_ONE_APP_SEARCH_USER_GCC_URL,
  ),
};

export const resolveOmsRegion = ({
  countryId,
  countryCode,
  isdCode,
} = {}) => {
  if (isdCode) {
    const regionFromIsd = OMS_REGION_BY_ISD_CODE[String(isdCode).trim()];
    if (regionFromIsd) {
      return regionFromIsd;
    }
  }

  if (countryCode) {
    const regionFromCode =
      OMS_REGION_BY_COUNTRY_CODE[String(countryCode).trim().toUpperCase()];
    if (regionFromCode) {
      return regionFromCode;
    }
  }

  if (countryId != null && countryId !== "") {
    const regionFromId = OMS_REGION_BY_COUNTRY_ID[Number(countryId)];
    if (regionFromId) {
      return regionFromId;
    }
  }

  return OMS_REGIONS.INDIA;
};

export const isLearnOnlyRegion = (options = {}) => {
  const region = resolveOmsRegion(options);
  return region === OMS_REGIONS.US || region === OMS_REGIONS.GCC;
};

/**
 * Live country context from the current URL.
 * Prefer this over session/local storage so a refresh always re-reads countryId.
 */
export const getCountryIdFromUrl = () => {
  if (typeof window === "undefined") {
    return null;
  }

  return new URLSearchParams(window.location.search).get("countryId");
};

const resolveRegionFromOptions = (options = {}) => {
  const countryId =
    options.countryId != null && options.countryId !== ""
      ? options.countryId
      : getCountryIdFromUrl();

  return resolveOmsRegion({
    countryId,
    countryCode: options.countryCode,
    isdCode: options.isdCode,
  });
};

const getRegionalUrl = ({
  options = {},
  urlByRegion,
  envKeyByRegion,
  serviceLabel,
}) => {
  const region = resolveRegionFromOptions(options);
  const baseUrl = urlByRegion[region];

  if (!baseUrl) {
    throw new Error(
      `${serviceLabel} URL is not configured for region "${region}". Set ${envKeyByRegion[region]}.`,
    );
  }

  return baseUrl;
};

/**
 * Resolves ONE OMS base URL by customer country / ISD.
 * Prefer env URLs per region; India keeps existing REACT_APP_ONE_OMS_URL.
 */
export const getOneOmsBaseUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: OMS_BASE_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_ONE_OMS_URL",
      [OMS_REGIONS.US]: "REACT_APP_ONE_OMS_US_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_ONE_OMS_GCC_URL",
    },
    serviceLabel: "ONE OMS",
  });

/**
 * Resolves unified search-user URL by customer country / ISD.
 * US staging: https://usdev-gatewayapi.devinfinitylearn.in/api/v1/unified/search-user
 */
export const getSearchUserUrl = (options = {}) =>
  getRegionalUrl({
    options,
    urlByRegion: SEARCH_USER_URL_BY_REGION,
    envKeyByRegion: {
      [OMS_REGIONS.INDIA]: "REACT_APP_ONE_APP_SEARCH_USER_URL",
      [OMS_REGIONS.US]: "REACT_APP_ONE_APP_SEARCH_USER_US_URL",
      [OMS_REGIONS.GCC]: "REACT_APP_ONE_APP_SEARCH_USER_GCC_URL",
    },
    serviceLabel: "Search user",
  });

/**
 * Packages are catalogued only on the India host, so every region reads from it.
 */
const getPackageApiBaseUrl = () => {
  const baseUrl = getIndiaPackageApiBaseUrl();

  if (!baseUrl) {
    throw new Error(
      "Package OMS API URL is not configured. Set REACT_APP_OMS_PACKAGE_API_BASE_URL.",
    );
  }

  return baseUrl;
};

export const getLearnPackagesUrl = () =>
  `${getPackageApiBaseUrl()}${PACKAGE_API_PATHS.LEARN_PACKAGES}`;

export const getScaPackagesUrl = () =>
  `${getPackageApiBaseUrl()}${PACKAGE_API_PATHS.SCA_PACKAGES}`;

export const getPackagesAndSubscriptionsUrl = () =>
  `${getPackageApiBaseUrl()}${PACKAGE_API_PATHS.PACKAGES_AND_SUBSCRIPTIONS}`;
